import {useEffect, useRef, useState} from 'react'
import {Link} from 'react-router-dom'
import axios from 'axios'
import Plotly from 'plotly.js-dist-min'
import {useAuth} from '../../modules/auth'
import {config} from '../../config'
import {POS_CONNECT} from '../../Helpers/constants'

// ChatBI always renders on Coal, so its text uses dark-surface colors in both portal themes
const MUTED_ON_COAL = '#9AA0A6'
const SOFT_ON_COAL = '#E2DED7'

// ─── Types ───────────────────────────────────────────────────────────────────

interface NarrativeBlock {
  text: string
  keyPoints?: string[]
  recommendations?: string[]
  caveats?: string[]
}

interface PlotlySpec {
  data: object[]
  layout: Record<string, unknown>
}

interface ChatBiResponse {
  node: string
  narrative: NarrativeBlock
  chart: {chartType: string; spec: PlotlySpec} | null
  data: {rows: Record<string, unknown>[]; schema: {col: string; type: string}[]}
  meta: {
    latencyMs: number
    sql: string
    cacheHit: boolean
    tokensIn: number
    tokensOut: number
    posConnectionRequired?: boolean
    connectPosPath?: string
  }
}

interface ReportCard {
  node: string
  title: string
  example: string
  icon: string
  requiresPosConnection?: boolean
}

interface ChatBiReportsPayload {
  reports: ReportCard[]
  posConnected: boolean
  posReportNodes: string[]
}

interface Message {
  role: 'user' | 'assistant'
  text: string
  response?: ChatBiResponse
  error?: string
}

// ─── Chart component (Plotly) ─────────────────────────────────────────────────

function hasChartData(spec: PlotlySpec): boolean {
  if (!spec?.data?.length) return false
  return spec.data.some((traceRaw) => {
    const trace = traceRaw as Record<string, unknown>
    const nums = (arr: unknown[] | undefined) =>
      arr?.length ? arr.some((v) => typeof v === 'number' ? v !== 0 : v != null) : false
    const x = trace.x as unknown[] | undefined
    const y = trace.y as unknown[] | undefined
    const values = trace.values as unknown[] | undefined
    const labels = trace.labels as unknown[] | undefined
    // labels alone (categories) don't count — need at least one non-zero numeric value
    if (values?.length) return nums(values)
    if (y?.length) return nums(y)
    if (x?.length) return nums(x)
    if (labels?.length) return false
    return false
  })
}

function ChartRenderer({spec, id}: {spec: PlotlySpec; id: string}) {
  const ref = useRef<HTMLDivElement>(null)

  const hasData = hasChartData(spec)

  useEffect(() => {
    if (ref.current && spec?.data && hasData) {
      Plotly.newPlot(
        ref.current,
        spec.data as Plotly.Data[],
        {
          ...spec.layout,
          autosize: true,
          height: 340,
          paper_bgcolor: 'transparent',
          plot_bgcolor: 'transparent',
          font: {color: '#9A968F', family: 'Instrument Sans, Helvetica, sans-serif'},
        } as Partial<Plotly.Layout>,
        {responsive: true, displayModeBar: false}
      )
    }
  }, [spec, hasData])

  if (!hasData) {
    return (
      <div
        className='rounded-2 d-flex flex-column align-items-center justify-content-center mt-4'
        style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.07)',
          height: 200,
          color: '#6B6864',
        }}
      >
        <span style={{fontSize: 32, marginBottom: 8}}>📊</span>
        <span style={{fontSize: 13}}>No data available for this period</span>
        <span style={{fontSize: 11, marginTop: 4, color: '#4A4744'}}>Try a wider date range or check that bookings exist</span>
      </div>
    )
  }

  return (
    <div className='rounded-2 overflow-hidden mt-4' style={{background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)'}}>
      <div ref={ref} id={id} style={{width: '100%'}} />
    </div>
  )
}

// ─── Insight card ─────────────────────────────────────────────────────────────

function InsightCard({narrative}: {narrative: NarrativeBlock}) {
  return (
    <div className='rounded-2 p-5 mt-3' style={{background: 'rgba(69,54,67,0.08)', border: '1px solid rgba(69,54,67,0.2)'}}>
      <p className='mb-3' style={{lineHeight: 1.7, color: SOFT_ON_COAL}}>{narrative.text}</p>
      {!!narrative.keyPoints?.length && (
        <ul className='ps-4 mb-3' style={{color: '#9A968F', fontSize: 13}}>
          {narrative.keyPoints.map((p, i) => <li key={i} className='mb-1'>{p}</li>)}
        </ul>
      )}
      {!!narrative.recommendations?.length && (
        <div className='mt-2'>
          <div className='text-success fw-semibold fs-7 mb-2'>Recommendations</div>
          <ul className='ps-4' style={{color: '#9A968F', fontSize: 13}}>
            {narrative.recommendations.map((r, i) => <li key={i} className='mb-1'>{r}</li>)}
          </ul>
        </div>
      )}
      {!!narrative.caveats?.length && (
        <div className='mt-2 text-warning fs-8'>⚠ {narrative.caveats.join(' ')}</div>
      )}
    </div>
  )
}

// ─── Node badge ───────────────────────────────────────────────────────────────

const NODE_LABELS: Record<string, string> = {
  'revenue-trend': 'Revenue Trend',
  'listing-mix': 'Listing Mix',
  'booking-funnel': 'Booking Funnel',
  'table-utilization': 'Table Utilization',
  'coupon-effectiveness': 'Coupon Effectiveness',
  'reservation-vs-fnb': 'Reservation vs F&B',
  'tip-health': 'Tip Health',
  'check-completion': 'Check Completion',
  generic: 'Explorer',
}

function NodeBadge({node}: {node: string}) {
  const isGeneric = node === 'generic'
  return (
    <span
      className='badge rounded-pill fs-9 fw-semibold ms-2'
      style={{
        background: isGeneric ? 'rgba(200,146,74,0.15)' : 'rgba(69,54,67,0.15)',
        color: isGeneric ? '#C8924A' : '#B4D6ED',
        border: `1px solid ${isGeneric ? 'rgba(200,146,74,0.3)' : 'rgba(69,54,67,0.3)'}`,
      }}
    >
      {NODE_LABELS[node] ?? node}
    </span>
  )
}

// ─── Message bubble ───────────────────────────────────────────────────────────

function MessageBubble({msg, index}: {msg: Message; index: number}) {
  const [showSql, setShowSql] = useState(false)

  if (msg.role === 'user') {
    return (
      <div className='d-flex justify-content-end mb-4'>
        <div
          className='px-5 py-3 rounded-3 text-white fs-6'
          style={{
            maxWidth: '72%',
            background: 'var(--bs-primary)',
            lineHeight: 1.6,
          }}
        >
          {msg.text}
        </div>
      </div>
    )
  }

  return (
    <div className='mb-6'>
      <div className='d-flex align-items-center mb-2'>
        <div
          className='rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0'
          style={{width: 32, height: 32, background: 'linear-gradient(135deg,#453643,#B4D6ED)', fontSize: 13, fontWeight: 700, color: '#fff'}}
        >
          BI
        </div>
        <span className='fs-8' style={{color: MUTED_ON_COAL}}>DIIBS Business Intelligence</span>
        {msg.response && <NodeBadge node={msg.response.node} />}
        {msg.response?.meta.cacheHit && (
          <span className='badge badge-light-success rounded-pill fs-9 ms-2'>cached</span>
        )}
      </div>

      {msg.error ? (
        <div className='alert alert-danger py-3 fs-7'>{msg.error}</div>
      ) : msg.response ? (
        <div className='ps-11'>
          <InsightCard narrative={msg.response.narrative} />
          {msg.response.chart && (
            <ChartRenderer spec={msg.response.chart.spec} id={`chart-${index}`} />
          )}
          {msg.response.node === 'generic' && (
            <button
              className='btn btn-sm btn-link p-0 mt-3 fs-8'
              style={{color: MUTED_ON_COAL}}
              onClick={() => setShowSql((v) => !v)}
            >
              {showSql ? 'Hide SQL' : 'Show SQL'}
            </button>
          )}
          {showSql && (
            <pre
              className='mt-2 p-3 rounded-2 fs-9 text-success'
              style={{background: 'rgba(0,0,0,0.3)', overflowX: 'auto', whiteSpace: 'pre-wrap'}}
            >
              {msg.response.meta.sql}
            </pre>
          )}
          <div className='fs-9 mt-2' style={{color: MUTED_ON_COAL}}>
            {msg.response.data.rows.length} rows · {msg.response.meta.latencyMs}ms
          </div>
        </div>
      ) : (
        <div className='ps-11' style={{color: MUTED_ON_COAL}}>{msg.text}</div>
      )}
    </div>
  )
}

// ─── Welcome / empty state ────────────────────────────────────────────────────

function WelcomeScreen({
  reports,
  posConnected,
  onPick,
}: {
  reports: ReportCard[]
  posConnected: boolean
  onPick: (q: string) => void
}) {
  const posLocked = (r: ReportCard) => Boolean(r.requiresPosConnection && !posConnected)

  return (
    <div className='d-flex flex-column align-items-center justify-content-center h-100 py-10'>
      <div
        className='rounded-circle d-flex align-items-center justify-content-center mb-6'
        style={{width: 72, height: 72, background: 'linear-gradient(135deg,#453643,#B4D6ED)', fontSize: 22, fontWeight: 800, color: '#fff'}}
      >
        BI
      </div>
      <h2 className='text-white fw-bold mb-2 text-center'>Chat to Business Intelligence</h2>
      <p className='mb-4 text-center' style={{maxWidth: 480, color: MUTED_ON_COAL}}>
        Ask anything about your restaurant — revenue, bookings, tables, coupons, and more. Get instant AI-powered insights with interactive charts.
      </p>

      {!posConnected && (
        <div
          className='rounded-2 px-5 py-4 mb-6 text-center'
          style={{
            maxWidth: 560,
            background: 'rgba(200,146,74,0.08)',
            border: '1px solid rgba(200,146,74,0.25)',
            color: '#E2C394',
            fontSize: 13,
          }}
        >
          Connect your POS system to unlock Reservation vs F&B Spend, Tip Rate &amp; Service, and POS Check
          Completion reports.
          <div className='mt-2'>
            <Link to={POS_CONNECT} className='text-warning fw-semibold fs-8'>
              Go to Connect POS →
            </Link>
          </div>
        </div>
      )}

      <div className='row g-3' style={{maxWidth: 720}}>
        {reports.map((r) => {
          const locked = posLocked(r)
          return (
            <div key={r.node} className='col-12 col-sm-6'>
              <button
                type='button'
                className='w-100 text-start rounded-2 p-4 border-0 position-relative'
                disabled={locked}
                style={{
                  background: locked ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  cursor: locked ? 'not-allowed' : 'pointer',
                  opacity: locked ? 0.45 : 1,
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e) => {
                  if (!locked) {
                    (e.currentTarget as HTMLButtonElement).style.background = 'rgba(69,54,67,0.12)'
                  }
                }}
                onMouseLeave={(e) => {
                  if (!locked) {
                    (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.04)'
                  }
                }}
                onClick={() => !locked && onPick(r.example)}
              >
                {locked && (
                  <span
                    className='badge rounded-pill position-absolute top-0 end-0 m-3 fs-9'
                    style={{background: 'rgba(200,146,74,0.2)', color: '#D4A86C'}}
                  >
                    Requires POS
                  </span>
                )}
                <div className='text-white fw-semibold fs-7 mb-1'>{r.title}</div>
                <div className='fs-8' style={{color: MUTED_ON_COAL}}>{r.example}</div>
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── PDF export ───────────────────────────────────────────────────────────────

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

async function chartImageForPdf(spec: PlotlySpec): Promise<string | null> {
  if (!hasChartData(spec)) return null
  try {
    return await Plotly.toImage(
      {
        data: spec.data as Plotly.Data[],
        layout: {
          ...spec.layout,
          autosize: false,
          width: 900,
          height: 380,
          paper_bgcolor: '#ffffff',
          plot_bgcolor: '#F7F5F2',
          font: {color: '#191C1F', family: 'Instrument Sans, Helvetica, sans-serif', size: 12},
        } as Partial<Plotly.Layout>,
      },
      {format: 'png', width: 900, height: 380, scale: 2}
    )
  } catch (err) {
    console.warn('[ChatBI] PDF chart export failed:', err)
    return null
  }
}

async function downloadPDF(msgs: Message[]): Promise<void> {
  const now = new Date().toLocaleString()
  let html =
    '<!DOCTYPE html><html><head><title>DIIBS BI Report</title>' +
    '<link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&display=swap" rel="stylesheet">' +
    '<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:Inter,sans-serif;color:#191C1F;padding:32px 40px;font-size:12px;line-height:1.6}' +
    '.header{text-align:center;border-bottom:2px solid #453643;padding-bottom:14px;margin-bottom:20px}' +
    '.header h1{font-size:20px;color:#453643;margin-bottom:2px}.header p{color:#6B6864;font-size:10px}' +
    '.q{background:#453643;color:#fff;padding:8px 14px;border-radius:8px;margin:8px 0;display:inline-block;max-width:70%;float:right;clear:both}' +
    '.clear{clear:both}.a{clear:both;background:#F7F5F2;padding:12px 16px;border-radius:8px;margin:10px 0;border:1px solid #E2DED7}' +
    '.kp{font-size:11px;color:#4A4744;padding:1px 0 1px 14px}.rec{font-size:10px;color:#4E7C6F;font-weight:600;margin:8px 0 3px}' +
    '.chart{page-break-inside:avoid;text-align:center;margin:12px 0}.chart img{max-width:100%;height:auto;border:1px solid #E2DED7;border-radius:8px}' +
    '.footer{margin-top:20px;border-top:1px solid #E2DED7;padding-top:8px;text-align:center;font-size:9px;color:#9A968F}' +
    '@media print{body{padding:20px}.chart img{max-width:100%}}</style></head><body>'
  html += `<div class="header"><h1>DIIBS BI Report</h1><p>${escapeHtml(now)}</p></div>`

  for (const m of msgs) {
    if (m.role === 'user') {
      html += `<div class="q">${escapeHtml(m.text)}</div><div class="clear"></div>`
      continue
    }
    if (!m.response) continue

    const {narrative, chart} = m.response
    html += `<div class="a"><p>${escapeHtml(narrative.text)}</p>`
    if (narrative.keyPoints?.length) {
      narrative.keyPoints.forEach((p) => { html += `<div class="kp">• ${escapeHtml(p)}</div>` })
    }
    if (narrative.recommendations?.length) {
      html += '<div class="rec">Recommendations</div>'
      narrative.recommendations.forEach((r) => { html += `<div class="kp">• ${escapeHtml(r)}</div>` })
    }
    if (narrative.caveats?.length) {
      narrative.caveats.forEach((c) => { html += `<div class="kp" style="color:#8F6630">⚠ ${escapeHtml(c)}</div>` })
    }

    const spec = chart?.spec
    if (spec) {
      const img = await chartImageForPdf(spec)
      if (img) {
        html += `<div class="chart"><img src="${img}" alt="Chart" /></div>`
      }
    }

    html += '</div>'
  }

  html += '<div class="footer">Generated by DIIBS Business Intelligence · Confidential</div></body></html>'
  const w = window.open('', '_blank')
  if (w) {
    w.document.write(html)
    w.document.close()
    setTimeout(() => w.print(), 500)
  }
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function ChatBI() {
  const {currentUser} = useAuth()
  const BASE_URL = config.apiUrl
  const authHeaders = {Authorization: `Bearer ${currentUser?.api_token}`}

  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [reports, setReports] = useState<ReportCard[]>([])
  const [posConnected, setPosConnected] = useState(true)
  const [pdfExporting, setPdfExporting] = useState(false)
  const endRef = useRef<HTMLDivElement>(null)

  const isReportLocked = (r: ReportCard) => Boolean(r.requiresPosConnection && !posConnected)

  useEffect(() => {
    axios
      .get(`${BASE_URL}/chatbi/reports`, {headers: authHeaders})
      .then((r) => {
        const payload = r.data?.data as ChatBiReportsPayload | ReportCard[] | undefined
        if (Array.isArray(payload)) {
          setReports(payload)
          setPosConnected(false)
          return
        }
        setReports(payload?.reports ?? [])
        setPosConnected(payload?.posConnected ?? false)
      })
      .catch(() => {})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    endRef.current?.scrollIntoView({behavior: 'smooth'})
  }, [messages, loading])

  const send = async (text?: string) => {
    const query = (text ?? input).trim()
    if (!query || loading) return
    setInput('')
    setMessages((prev) => [...prev, {role: 'user', text: query}])
    setLoading(true)

    const history = messages.slice(-8).map((m) => ({
      role: m.role,
      content: m.role === 'user' ? m.text : m.response?.narrative.text ?? m.text,
    }))

    try {
      const {data} = await axios.post(
        `${BASE_URL}/chatbi/query`,
        {query, conversationHistory: history},
        {headers: authHeaders}
      )
      const response: ChatBiResponse = data?.data
      setMessages((prev) => [
        ...prev,
        {role: 'assistant', text: response.narrative.text, response},
      ])
    } catch (err: unknown) {
      const msg = (err as {response?: {data?: {message?: string}}})?.response?.data?.message ?? 'Failed to process query. Please try again.'
      setMessages((prev) => [...prev, {role: 'assistant', text: msg, error: msg}])
    }
    setLoading(false)
  }

  return (
    <div
      className='d-flex flex-column'
      style={{height: 'calc(100vh - 65px)', background: '#101214', color: '#ECE9E4'}}
    >
      {/* Header */}
      <div
        className='d-flex align-items-center justify-content-between px-6 py-4 flex-shrink-0'
        style={{borderBottom: '1px solid rgba(255,255,255,0.07)'}}
      >
        <div className='d-flex align-items-center gap-3'>
          <div
            className='rounded-circle d-flex align-items-center justify-content-center'
            style={{width: 36, height: 36, background: 'linear-gradient(135deg,#453643,#B4D6ED)', fontSize: 12, fontWeight: 800, color: '#fff'}}
          >
            BI
          </div>
          <div>
            <div className='text-white fw-bold fs-6'>Chat Business Intelligence</div>
            <div className='fs-8' style={{color: MUTED_ON_COAL}}>
              Ask anything about your restaurant performance
            </div>
          </div>
        </div>
        {messages.length > 0 && (
          <div className='d-flex gap-3'>
            <button
              className='btn btn-sm btn-light-danger py-2 px-4 fs-8'
              onClick={() => setMessages([])}
            >
              Clear
            </button>
            <button
              className='btn btn-sm btn-light-primary py-2 px-4 fs-8'
              disabled={pdfExporting}
              onClick={() => {
                setPdfExporting(true)
                void downloadPDF(messages).finally(() => setPdfExporting(false))
              }}
            >
              {pdfExporting ? 'Preparing…' : 'Download PDF'}
            </button>
          </div>
        )}
      </div>

      {/* Chat area */}
      <div className='flex-grow-1 overflow-auto px-6 py-6'>
        {messages.length === 0 && !loading ? (
          <WelcomeScreen reports={reports} posConnected={posConnected} onPick={send} />
        ) : (
          <>
            {messages.map((m, i) => (
              <MessageBubble key={i} msg={m} index={i} />
            ))}
            {loading && (
              <div className='d-flex align-items-center gap-3 mb-6'>
                <div
                  className='rounded-circle d-flex align-items-center justify-content-center flex-shrink-0'
                  style={{width: 32, height: 32, background: 'linear-gradient(135deg,#453643,#B4D6ED)', fontSize: 13, fontWeight: 700, color: '#fff'}}
                >
                  BI
                </div>
                <div className='d-flex gap-2 align-items-center'>
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className='rounded-circle'
                      style={{
                        width: 8, height: 8,
                        background: '#B4D6ED',
                        animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite`,
                      }}
                    />
                  ))}
                </div>
              </div>
            )}
            <div ref={endRef} />
          </>
        )}
      </div>

      {/* Input bar */}
      <div
        className='px-6 py-4 flex-shrink-0'
        style={{borderTop: '1px solid rgba(255,255,255,0.07)', background: '#101214'}}
      >
        {/* Quick-access chips */}
        {messages.length === 0 && reports.length > 0 && (
          <div className='d-flex gap-2 flex-wrap mb-3'>
            {reports
              .filter((r) => !isReportLocked(r))
              .slice(0, 4)
              .map((r) => (
                <button
                  key={r.node}
                  className='btn btn-sm rounded-pill py-1 px-3 fs-8'
                  style={{background: 'rgba(69,54,67,0.12)', color: '#B4D6ED', border: '1px solid rgba(69,54,67,0.25)'}}
                  onClick={() => send(r.example)}
                  disabled={loading}
                >
                  {r.title}
                </button>
              ))}
          </div>
        )}

        <div className='d-flex gap-3'>
          <input
            className='form-control rounded-3 fs-7'
            style={{
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.12)',
              color: '#ECE9E4',
              padding: '12px 16px',
            }}
            placeholder='Ask about your restaurant performance…'
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && send()}
            disabled={loading}
          />
          <button
            className='btn btn-primary rounded-3 px-6 fw-semibold fs-7 flex-shrink-0'
            onClick={() => send()}
            disabled={loading || !input.trim()}
            style={{border: 'none', minWidth: 90}}
          >
            {loading ? '…' : 'Send'}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50%       { opacity: 1;   transform: scale(1.1); }
        }
        .form-control::placeholder { color: #4A4744; }
        .form-control:focus {
          background: rgba(255,255,255,0.09) !important;
          border-color: rgba(69,54,67,0.5) !important;
          box-shadow: 0 0 0 3px rgba(69,54,67,0.15) !important;
          color: #ECE9E4 !important;
        }
      `}</style>
    </div>
  )
}
